/**
 * KAIZEN Marketing Studio — Pipeline 10 publications/jour
 *
 * Vue principale pour Kru Ralph :
 * - Calendrier visuel 14 jours (5 slots/jour)
 * - Aperçu caption + image générée IA
 * - Approve / Schedule / Publish-now / Regenerate
 * - Stats funnel leads
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Calendar, Sparkle, CheckCircle, ArrowsClockwise, PaperPlaneTilt, Trash, Image as ImageIcon, ClockCounterClockwise, Eye } from "@phosphor-icons/react";
import PublishingPauseBanner from "../components/PublishingPauseBanner";
import { GraphicFilesPanel } from "../components/marketing/GraphicFilesPanel";

const DISCIPLINE_LABEL = {
  muay_thai: "Muay Thaï",
  kickboxing: "Kickboxing",
  jiu_jitsu_bresilien: "BJJ",
  jujutsu_japonais: "Jiu-jitsu Can Ryu",
  all: "Toutes",
};

const STATUS_COLOR = {
  draft: "zinc",
  approved: "amber",
  scheduled: "sky",
  published: "emerald",
  failed: "rose",
};

export default function MarketingStudioModule() {
  const [pubs, setPubs] = useState([]);
  const [stats, setStats] = useState({});
  const [leadsStats, setLeadsStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDay, setFilterDay] = useState("all");
  const [generating, setGenerating] = useState(false);
  const [previewPub, setPreviewPub] = useState(null);
  const [pending, setPending] = useState([]);
  const [rejections, setRejections] = useState([]);
  const [showRejections, setShowRejections] = useState(false);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "graphistes" ? "graphistes" : "pipeline"));

  useEffect(() => { load(); loadLeads(); loadPending(); loadRejections(); }, []);

  async function loadPending() {
    try {
      const r = await api.get("/marketing/pending-approval");
      setPending(r.data.items || []);
    } catch (e) { /* ignore */ }
  }

  async function loadRejections() {
    try {
      const r = await api.get("/marketing/rejections");
      setRejections(r.data.items || []);
    } catch (e) { /* ignore */ }
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/marketing/calendrier");
      setPubs(r.data.publications || []);
      setStats(r.data.stats || {});
    } catch (e) {
      toast.error("Erreur chargement: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  async function loadLeads() {
    try {
      const r = await api.get("/funnel/leads");
      setLeadsStats(r.data.stats || {});
    } catch (e) { /* ignore */ }
  }

  async function generateCalendar() {
    if (!confirm("Générer 14 jours = 70 nouvelles publications. Continuer ?")) return;
    setGenerating(true);
    try {
      const r = await api.post("/marketing/calendrier/generer", {
        days: 14, slots_per_day: 5, auto_approve: false,
        publish_targets: ["facebook", "instagram_feed"],
      });
      toast.success(`✅ ${r.data.publications_created} publications créées — génération IA en cours`);
      setTimeout(load, 3000);
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    }
    setGenerating(false);
  }

  async function fillBacklog() {
    if (!confirm("Générer légende + visuel IA pour les prochaines publications VIDES (à venir) ?\n\nChaque publication coûte des crédits IA (~15-30 sec / pub). Lance par lots.")) return;
    setGenerating(true);
    try {
      const r = await api.post("/marketing/calendrier/fill-backlog?limit=14");
      toast.success(`🪄 ${r.data.queued} publication(s) en génération · ${r.data.remaining_empty_after} vides restantes`);
      setTimeout(load, 30000);
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    }
    setGenerating(false);
  }

  async function regenerate(pubId) {
    try {
      await api.post(`/marketing/publication/${pubId}/generate-content`);
      toast.success("Régénération en cours (15-30 sec)…");
      setTimeout(() => { load(); loadPending(); }, 25000);
    } catch (e) {
      toast.error("Erreur");
    }
  }

  async function approve(pubId) {
    try {
      await api.post(`/marketing/publication/${pubId}/approve`);
      toast.success("Approuvée ✓ — sera publiée à l'heure programmée");
      load(); loadPending();
    } catch (e) { toast.error("Erreur"); }
  }

  async function rejectPub(pubId) {
    if (!confirm("Rejeter (bloquer) cette publication ? Elle ne sera jamais publiée.")) return;
    try {
      await api.post(`/marketing/publication/${pubId}/reject`);
      toast.success("Rejetée ✓");
      load(); loadPending(); loadRejections();
    } catch (e) { toast.error("Erreur"); }
  }

  async function publishNow(pubId) {
    if (!confirm("Publier MAINTENANT sur Facebook + Instagram ?")) return;
    try {
      const r = await api.post(`/marketing/publication/${pubId}/publish-now`, {});
      if (r.data.ok) {
        toast.success("🚀 Publication LIVE sur FB + IG !");
      } else {
        toast.error("Publication partielle — vérifie les résultats");
      }
      load();
    } catch (e) {
      toast.error("Erreur publication: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function deletePub(pubId) {
    if (!confirm("Supprimer cette publication ?")) return;
    try {
      await api.delete(`/marketing/publication/${pubId}`);
      toast.success("Supprimée");
      load();
    } catch (e) { toast.error("Erreur"); }
  }

  const grouped = useMemo(() => {
    const g = {};
    pubs.forEach((p) => {
      if (filterStatus && p.status !== filterStatus) return;
      const dayKey = (p.scheduled_at || "").slice(0, 10);
      if (filterDay !== "all" && filterDay !== dayKey) return;
      g[dayKey] = g[dayKey] || [];
      g[dayKey].push(p);
    });
    return g;
  }, [pubs, filterStatus, filterDay]);

  const allDays = useMemo(() => [...new Set(pubs.map((p) => (p.scheduled_at || "").slice(0, 10)))].sort(), [pubs]);

  return (
    <div className="max-w-7xl mx-auto pb-12" data-testid="marketing-studio">
      <PublishingPauseBanner />
      <div className="mb-5 flex gap-2 text-sm" data-testid="ms-tabs">
        <button onClick={() => setTab("pipeline")} data-testid="ms-tab-pipeline" className={`px-4 py-2 rounded-lg ${tab === "pipeline" ? "bg-amber-500 text-zinc-950 font-semibold" : "bg-zinc-900 border border-zinc-800 text-zinc-300"}`}>Pipeline publications</button>
        <button onClick={() => setTab("graphistes")} data-testid="ms-tab-graphistes" className={`px-4 py-2 rounded-lg ${tab === "graphistes" ? "bg-amber-500 text-zinc-950 font-semibold" : "bg-zinc-900 border border-zinc-800 text-zinc-300"}`}>Fichiers graphistes (vectoriel)</button>
      </div>
      {tab === "graphistes" && (
        <div data-testid="ms-graphistes">
          <h1 className="text-2xl font-bold text-zinc-100 mb-1">Fichiers graphistes — logos & visuels vectoriels</h1>
          <p className="text-sm text-zinc-400 mb-5">Téléverse ou crée un visuel, obtiens le SVG / PDF / PNG HD pour la marchandise promo, envoie-le au fournisseur et suis les réponses.</p>
          <GraphicFilesPanel />
        </div>
      )}
      {tab === "pipeline" && (<>
      {/* Header */}
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-zinc-100 flex items-center gap-2">
            <Megaphone size={32} className="text-amber-400" weight="fill" />
            Marketing Studio
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Pipeline 10 publications/jour · Facebook + Instagram · Voix Camille
          </p>
        </div>
        <button
          onClick={generateCalendar}
          disabled={generating}
          data-testid="btn-generate-calendar"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Sparkle size={18} weight="bold" />
          {generating ? "Génération…" : "Générer 14 jours"}
        </button>
        <button
          onClick={fillBacklog}
          disabled={generating}
          data-testid="btn-fill-backlog"
          className="bg-zinc-800 hover:bg-zinc-700 border border-amber-500/40 disabled:opacity-50 text-amber-300 font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Sparkle size={18} weight="bold" />
          Remplir le backlog (14)
        </button>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5">
        <StatCard label="Total" value={pubs.length} color="zinc" />
        <StatCard label="Draft" value={stats.draft || 0} color="zinc" />
        <StatCard label="Approuvées" value={stats.approved || 0} color="amber" />
        <StatCard label="Programmées" value={stats.scheduled || 0} color="sky" />
        <StatCard label="Publiées" value={stats.published || 0} color="emerald" />
        <StatCard label="Échouées" value={stats.failed || 0} color="rose" />
      </div>

      {/* Funnel Leads */}
      <div className="bg-gradient-to-br from-amber-500/10 to-zinc-900 border border-amber-500/30 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-amber-400 font-semibold flex items-center gap-2">
            🎯 Funnel cours d'essai (19,98 $) — Leads captés
          </div>
          <div className="text-xs text-zinc-500">
            URL publique : <code className="text-amber-300">/essai-gratuit</code>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <FunnelStat label="Nouveaux" value={leadsStats.new || 0} />
          <FunnelStat label="Contactés" value={leadsStats.contacted || 0} />
          <FunnelStat label="Essai réservé" value={leadsStats.trial_booked || 0} />
          <FunnelStat label="Convertis ✓" value={leadsStats.converted || 0} highlight />
          <FunnelStat label="Perdus" value={leadsStats.lost || 0} />
        </div>
      </div>

      {/* À approuver — garde-fou validation visuelle IA */}
      <PendingApprovalSection
        items={pending}
        onApprove={approve}
        onRegenerate={regenerate}
        onReject={rejectPub}
        onPreview={setPreviewPub}
      />

      <RejectionHistorySection
        items={rejections}
        open={showRejections}
        onToggle={() => setShowRejections((v) => !v)}
      />

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-4 flex gap-2 flex-wrap">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
          data-testid="filter-status"
        >
          <option value="">Tous les statuts</option>
          <option value="draft">Draft</option>
          <option value="approved">Approuvées</option>
          <option value="scheduled">Programmées</option>
          <option value="published">Publiées</option>
          <option value="failed">Échouées</option>
        </select>
        <select
          value={filterDay}
          onChange={(e) => setFilterDay(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
          data-testid="filter-day"
        >
          <option value="all">Tous les jours</option>
          {allDays.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="text-zinc-500 text-center py-8">Chargement…</div>
      ) : pubs.length === 0 ? (
        <div className="text-zinc-500 text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <Calendar size={48} className="mx-auto mb-3 text-zinc-700" />
          <p>Aucune publication. Clique "Générer 14 jours" pour démarrer le pipeline.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([day, dayPubs]) => (
            <div key={day} className="space-y-2">
              <div className="text-zinc-300 font-semibold text-sm uppercase tracking-wider px-1 flex items-center gap-2">
                <Calendar size={14} /> {day} ({dayPubs.length} slots)
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {dayPubs.map((p) => <PubCard
                  key={p.id} pub={p}
                  onPreview={() => setPreviewPub(p)}
                  onRegenerate={() => regenerate(p.id)}
                  onApprove={() => approve(p.id)}
                  onPublish={() => publishNow(p.id)}
                  onDelete={() => deletePub(p.id)}
                />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewPub && <PreviewModal pub={previewPub} onClose={() => setPreviewPub(null)} />}
      </>)}
    </div>
  );
}

const VERDICT = {
  conforme: { label: "Conforme", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  a_rejeter: { label: "À rejeter", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  non_verifie: { label: "Non vérifié", cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" },
};

function PendingApprovalSection({ items, onApprove, onRegenerate, onReject, onPreview }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-4 mb-5" data-testid="pending-approval-section">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-amber-400 font-semibold text-lg">🛡️ À approuver</span>
        <span className="text-xs text-zinc-400">
          {items.length} publication(s) — validation visuelle IA · aucune publication automatique
        </span>
      </div>
      <div className="space-y-3">
        {items.map((p) => {
          const v = p.validation || {};
          const verdict = VERDICT[v.status] || VERDICT.non_verifie;
          return (
            <div key={p.id} className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`pending-${p.id}`}>
              <div className="flex flex-col sm:flex-row">
                <div className="w-full sm:w-40 h-40 flex-shrink-0 bg-zinc-900 flex items-center justify-center">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => onPreview(p)} />
                  ) : (
                    <ImageIcon size={32} className="text-zinc-700" />
                  )}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-zinc-100 text-sm font-semibold">{p.angle_label || "Publication"}</div>
                    <div className="flex items-center gap-1.5">
                      {typeof (p.validation || {}).confidence === "number" && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                          v.confidence >= 75 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                          : v.confidence >= 45 ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                          : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`} data-testid={`confidence-${p.id}`}>
                          {v.confidence}% cohérent
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${verdict.cls}`} data-testid={`verdict-${p.id}`}>
                        {verdict.label}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                    <div><span className="text-zinc-500">Discipline attendue :</span> <span className="text-zinc-200">{p.discipline_label}</span></div>
                    <div><span className="text-zinc-500">Public cible :</span> <span className="text-zinc-200">{p.audience}</span></div>
                    <div className="col-span-2"><span className="text-zinc-500">Visuel détecté par IA :</span> <span className="text-zinc-300 italic">{v.detected || "—"}</span></div>
                    {v.motif && (
                      <div className="col-span-2"><span className="text-rose-400">Motif de rejet :</span> <span className="text-rose-300">{v.motif}</span></div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button onClick={() => onApprove(p.id)} data-testid={`approve-pending-${p.id}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                      <CheckCircle size={14} weight="bold" /> Approuver
                    </button>
                    <button onClick={() => onPreview(p)} data-testid={`preview-pending-${p.id}`}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                      <Eye size={14} weight="bold" /> Aperçu réseaux
                    </button>
                    <button onClick={() => onRegenerate(p.id)} data-testid={`regenerate-pending-${p.id}`}
                      className="bg-sky-700 hover:bg-sky-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                      <ArrowsClockwise size={14} weight="bold" /> Régénérer
                    </button>
                    <button onClick={() => onReject(p.id)} data-testid={`reject-pending-${p.id}`}
                      className="bg-rose-700 hover:bg-rose-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                      <Trash size={14} weight="bold" /> Rejeter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RejectionHistorySection({ items, open, onToggle }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-5" data-testid="rejection-history-section">
      <button onClick={onToggle} data-testid="toggle-rejections" className="w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-2">
          <ClockCounterClockwise size={18} className="text-rose-400" />
          <span className="text-zinc-200 font-semibold text-sm">Historique des rejets</span>
          <span className="text-xs text-zinc-500">{items.length} image(s) rejetée(s) — pour affiner les prompts</span>
        </span>
        <span className="text-zinc-500 text-xs">{open ? "▲ masquer" : "▼ afficher"}</span>
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((r) => (
            <div key={r.id} className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden flex" data-testid={`rejection-${r.id}`}>
              {r.image_url ? (
                <img src={r.image_url} alt="" className="w-24 h-24 object-cover flex-shrink-0 opacity-70" />
              ) : (
                <div className="w-24 h-24 bg-zinc-900 flex items-center justify-center flex-shrink-0"><ImageIcon size={24} className="text-zinc-700" /></div>
              )}
              <div className="p-2 min-w-0 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-300 font-semibold">{r.discipline_label || r.discipline}</span>
                  <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full border ${r.source === "manuel" ? "border-amber-500/40 text-amber-300" : "border-rose-500/40 text-rose-300"}`}>
                    {r.source === "manuel" ? "Rejet manuel" : "Rejet IA"}
                  </span>
                </div>
                <div className="text-rose-300 mt-1">{r.motif || "—"}</div>
                <div className="text-zinc-500 italic mt-0.5 line-clamp-2">{r.detected}</div>
                <div className="text-zinc-600 mt-0.5">{(r.rejected_at || "").slice(0, 10)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {  const ring = {
    zinc: "border-zinc-700 text-zinc-300",
    amber: "border-amber-500/40 text-amber-300",
    sky: "border-sky-500/40 text-sky-300",
    emerald: "border-emerald-500/40 text-emerald-300",
    rose: "border-rose-500/40 text-rose-300",
  }[color];
  return (
    <div className={`bg-zinc-900 border ${ring} rounded-xl p-3 text-center`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function FunnelStat({ label, value, highlight }) {
  return (
    <div className={`bg-zinc-950 border ${highlight ? "border-emerald-500/50" : "border-zinc-800"} rounded-lg p-2 text-center`}>
      <div className={`text-xl font-bold ${highlight ? "text-emerald-400" : "text-zinc-200"}`}>{value}</div>
      <div className="text-[9px] uppercase text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function PubCard({ pub, onPreview, onRegenerate, onApprove, onPublish, onDelete }) {
  const statusColor = STATUS_COLOR[pub.status] || "zinc";
  const time = (pub.scheduled_at || "").slice(11, 16);
  const hasCaption = !!pub.caption;
  const hasImage = !!pub.image_url;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-amber-500/40 transition" data-testid={`pub-${pub.id}`}>
      <div className="flex">
        {/* Image */}
        <div className="w-32 h-32 flex-shrink-0 bg-zinc-950 flex items-center justify-center">
          {hasImage ? (
            <img src={pub.image_url} alt="" className="w-full h-full object-cover cursor-pointer" onClick={onPreview} />
          ) : (
            <ImageIcon size={32} className="text-zinc-700" />
          )}
        </div>
        {/* Content */}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-xs text-amber-400 font-semibold flex items-center gap-1.5">
              <ClockCounterClockwise size={12} />
              {time} · {DISCIPLINE_LABEL[pub.discipline] || pub.discipline}
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-${statusColor}-500/10 text-${statusColor}-400 border border-${statusColor}-500/30`}>
              {pub.status}
            </span>
          </div>
          <div className="text-zinc-100 text-sm font-medium truncate">{pub.angle_label}</div>
          <div className="text-xs text-zinc-400 mt-1 line-clamp-2">
            {hasCaption ? pub.caption.split("\n").slice(0, 2).join(" · ") : <span className="text-zinc-600 italic">⏳ Contenu en cours de génération…</span>}
          </div>
          <div className="flex gap-1 mt-2">
            <button onClick={onPreview} disabled={!hasCaption && !hasImage} title="Aperçu" className="p-1.5 text-zinc-400 hover:text-amber-400 disabled:opacity-30">
              <ImageIcon size={16} />
            </button>
            <button onClick={onRegenerate} title="Régénérer" className="p-1.5 text-zinc-400 hover:text-sky-400">
              <ArrowsClockwise size={16} />
            </button>
            {pub.status === "draft" && (
              <button onClick={onApprove} disabled={!hasCaption} title="Approuver" className="p-1.5 text-zinc-400 hover:text-amber-400 disabled:opacity-30" data-testid={`approve-${pub.id}`}>
                <CheckCircle size={16} />
              </button>
            )}
            {(pub.status === "approved" || pub.status === "draft") && hasCaption && (
              <button onClick={onPublish} title="Publier maintenant" className="p-1.5 text-zinc-400 hover:text-emerald-400" data-testid={`publish-${pub.id}`}>
                <PaperPlaneTilt size={16} weight="fill" />
              </button>
            )}
            <button onClick={onDelete} title="Supprimer" className="p-1.5 text-zinc-400 hover:text-rose-400 ml-auto">
              <Trash size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ pub, onClose }) {
  const [net, setNet] = useState("facebook");
  const caption = pub.caption || "(pas de caption générée)";
  const targets = pub.targets || pub.publish_targets || ["facebook", "instagram_feed"];
  const disc = DISCIPLINE_LABEL[pub.discipline] || pub.discipline_label || pub.discipline;
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="social-preview-modal">
      <div className="bg-zinc-950 border border-amber-500/30 rounded-xl max-w-md w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <div>
            <div className="text-amber-400 text-sm font-semibold">Aperçu réel · {pub.angle_label}</div>
            <div className="text-xs text-zinc-500">{pub.scheduled_at?.slice(0, 16)} · {disc}</div>
          </div>
          <button onClick={onClose} data-testid="close-preview" className="text-zinc-400 hover:text-zinc-100 px-3">✕</button>
        </div>
        {/* Sélecteur de réseau */}
        <div className="flex gap-2 p-3">
          <button onClick={() => setNet("facebook")} data-testid="preview-tab-facebook"
            className={`flex-1 text-xs font-semibold py-1.5 rounded-lg ${net === "facebook" ? "bg-[#1877F2] text-white" : "bg-zinc-800 text-zinc-400"}`}>Facebook</button>
          <button onClick={() => setNet("instagram")} data-testid="preview-tab-instagram"
            className={`flex-1 text-xs font-semibold py-1.5 rounded-lg ${net === "instagram" ? "bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white" : "bg-zinc-800 text-zinc-400"}`}>Instagram</button>
        </div>
        {/* Carte de prévisualisation */}
        <div className="px-3 pb-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg text-black">
            <div className="flex items-center gap-2 p-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-600 to-amber-500 flex items-center justify-center text-white text-xs font-bold">II</div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold text-neutral-900">Institut Impact Élite Martial</div>
                <div className="text-[11px] text-neutral-500">{net === "instagram" ? "Sponsorisé" : "Publication · Sponsorisé"}</div>
              </div>
            </div>
            {net === "facebook" && (
              <div className="px-3 pb-2 text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug max-h-40 overflow-auto">{caption}</div>
            )}
            {pub.image_url ? (
              <img src={pub.image_url} alt="" className="w-full aspect-square object-cover bg-neutral-100" data-testid="preview-image" />
            ) : (
              <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center text-neutral-400 text-sm">Image en cours…</div>
            )}
            {net === "instagram" ? (
              <div className="px-3 py-2">
                <div className="text-[18px] mb-1">♥ ✉ ➤</div>
                <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug max-h-32 overflow-auto">
                  <span className="font-semibold">institut.impact </span>{caption}
                </div>
              </div>
            ) : (
              <div className="border-t border-neutral-200 px-3 py-2 flex justify-around text-[13px] text-neutral-600 font-medium">
                <span>👍 J'aime</span><span>💬 Commenter</span><span>↗ Partager</span>
              </div>
            )}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 text-center">
            Aperçu indicatif — cibles : {Array.isArray(targets) ? targets.join(", ") : targets}
          </p>
        </div>
      </div>
    </div>
  );
}
